/**
 * User Story Validation Service
 * 
 * Validates claims against parsed user story requirements to ensure compliance
 * with business rules and submission timelines.
 */

/**
 * User Story Error Codes
 */
export const USER_STORY_ERRORS = {
  CI_LATE_SUBMISSION: 'Critical Illness claims must be submitted within 3 months of diagnosis',
  DISABILITY_LATE_DIAGNOSIS: 'Disability must occur within 12 months of injury date',
  MISSING_REQUIRED_DOCUMENTS: 'Required documents per user story are missing',
  INCOMPLETE_TYPE_SPECIFIC_FIELDS: 'All type-specific fields must be completed before benefit calculation',
  INVALID_CLAIM_TIMELINE: 'Claim does not meet timeline requirements for this claim type',
  MISSING_MEDICAL_VALIDATION: 'Medical certificate validation required for this claim type'
};

/**
 * Validate Critical Illness claim against user story requirements
 */
export const validateCriticalIllnessClaim = (claimData) => {
  const errors = [];
  
  // Check required fields for CI claims
  if (!claimData.diagnosisDate) {
    errors.push('Diagnosis date is required for Critical Illness claims');
  }
  
  if (!claimData.conditionSeverityStage) {
    errors.push('Condition severity stage is required for Critical Illness claims');
  }
  
  // Validate 3-month submission timeline
  if (claimData.diagnosisDate) {
    const diagnosisDate = new Date(claimData.diagnosisDate);
    const currentDate = new Date();
    const monthsDiff = (currentDate - diagnosisDate) / (1000 * 60 * 60 * 24 * 30);
    
    if (monthsDiff > 3) {
      errors.push(USER_STORY_ERRORS.CI_LATE_SUBMISSION);
    }
  }
  
  // Check for required medical information
  if (!claimData.diagnosis) {
    errors.push('Medical diagnosis is required for Critical Illness claims');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate Disability claim against user story requirements
 */
export const validateDisabilityClaim = (claimData) => {
  const errors = [];
  
  // Check required fields for Disability claims
  if (!claimData.disabilityType) {
    errors.push('Disability type (PTD/TTD) is required for Disability claims');
  }
  
  if (!claimData.dateOfInjury) {
    errors.push('Date of injury is required for Disability claims');
  }
  
  // Validate 12-month injury to diagnosis timeline
  if (claimData.dateOfInjury && claimData.diagnosisDate) {
    const injuryDate = new Date(claimData.dateOfInjury);
    const diagnosisDate = new Date(claimData.diagnosisDate);
    const monthsDiff = (diagnosisDate - injuryDate) / (1000 * 60 * 60 * 24 * 30);
    
    if (monthsDiff > 12) {
      errors.push(USER_STORY_ERRORS.DISABILITY_LATE_DIAGNOSIS);
    }
  }
  
  // TTD specific validations
  if (claimData.disabilityType === 'TTD') {
    if (!claimData.estimatedPaymentDuration) {
      errors.push('Estimated payment duration is required for TTD claims');
    } else if (claimData.estimatedPaymentDuration > 6) {
      errors.push('TTD claims cannot exceed 6 months duration');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate Death claim against user story requirements
 */
export const validateDeathClaim = (claimData) => {
  const errors = [];
  
  // Check required fields for Death claims
  if (!claimData.deceasedDateOfDeath) {
    errors.push('Date of death is required for Death claims');
  }
  
  if (!claimData.deceasedDateOfBirth) {
    errors.push('Date of birth of deceased is required for Death claims');
  }
  
  if (!claimData.causeOfDeath) {
    errors.push('Cause of death is required for Death claims');
  }
  
  // Validate death date is not in the future
  if (claimData.deceasedDateOfDeath) {
    const deathDate = new Date(claimData.deceasedDateOfDeath);
    const currentDate = new Date();
    
    if (deathDate > currentDate) {
      errors.push('Date of death cannot be in the future');
    }
  }
  
  // Validate birth date is before death date
  if (claimData.deceasedDateOfBirth && claimData.deceasedDateOfDeath) {
    const birthDate = new Date(claimData.deceasedDateOfBirth);
    const deathDate = new Date(claimData.deceasedDateOfDeath);
    
    if (birthDate >= deathDate) {
      errors.push('Date of birth must be before date of death');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Check if all required fields for benefit calculation are present
 */
export const validateRequiredFieldsForBenefitCalculation = (claimData) => {
  if (!claimData.type || !claimData.sumAssured) {
    return {
      isValid: false,
      errors: ['Claim type and sum assured are required for benefit calculation']
    };
  }
  
  // Type-specific validation
  switch (claimData.type.toUpperCase()) {
    case '002': // Critical Illness
      return validateCriticalIllnessClaim(claimData);
    case 'PTD': // Permanent Total Disability
    case 'TTD': // Temporary Total Disability
      return validateDisabilityClaim(claimData);
    case 'DTH': // Death
    case '010': // Spousal Cover
    case '012': // Coffin Cover  
    case '013': // Parent Cover
      return validateDeathClaim(claimData);
    case '005': // Medical
      return { isValid: true, errors: [] }; // Medical claims have different validation rules
    default:
      return {
        isValid: false,
        errors: ['Unknown claim type']
      };
  }
};

/**
 * Main validation function that validates claim against user story requirements
 */
export const validateClaimAgainstUserStory = async (claimData) => {
  const validation = validateRequiredFieldsForBenefitCalculation(claimData);
  
  return {
    isValid: validation.isValid,
    errors: validation.errors,
    claimType: claimData.type,
    validationTimestamp: new Date().toISOString()
  };
};

/**
 * Timeline validation utilities
 */
export const validateSubmissionTimeline = (type, dates) => {
  switch (type?.toUpperCase()) {
    case '002': { // Critical Illness
      if (!dates.diagnosis || !dates.submission) return false;
      const ciMonthsDiff = (new Date(dates.submission) - new Date(dates.diagnosis)) / (1000 * 60 * 60 * 24 * 30);
      return ciMonthsDiff <= 3;
    }
      
    case 'PTD': // Permanent Total Disability
    case 'TTD': { // Temporary Total Disability
      if (!dates.injury || !dates.diagnosis) return false;
      const disMonthsDiff = (new Date(dates.diagnosis) - new Date(dates.injury)) / (1000 * 60 * 60 * 24 * 30);
      return disMonthsDiff <= 12;
    }
      
    default:
      return true; // No specific timeline for other claim types
  }
};

/**
 * Check if user has completed minimum required fields for payout preview
 */
export const canShowPayoutPreview = (formData, currentStep) => {
  // Must have basic claim info
  if (!formData.type || !formData.sumAssured) {
    return false;
  }
  
  // Must be at least on step 3 (type-specific details) or later
  if (currentStep < 3) {
    return false;
  }
  
  // Validate against user story requirements
  const validation = validateRequiredFieldsForBenefitCalculation(formData);
  return validation.isValid;
};
